import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useSaleSelection } from '../hooks/useSaleSelection';
import SaleContextBanner from '../components/admin/SaleContextBanner';

/*
 * This file used to open with:
 *
 *   // The auction currently being shipped. When this becomes multi-auction,
 *   // promote to a selector like PickerInterface does.
 *   const CURRENT_AUCTION_ID = 'ATyU38t6jJB5s5107kRX';
 *
 * It became multi-auction in June. The constant did not, and there was no
 * selector and no banner, so the page kept rendering that sale's own title and
 * looked entirely correct while shipping against a closed sale for two months.
 *
 * The sale now comes from useSaleSelection with role 'fulfillment' — the most
 * recently CLOSED sale, never a live one, since the boxes on the bench are
 * always from a sale that has finished. It is switchable, and the banner names
 * it on screen at all times.
 */

// Phase 1: we open PirateShip and drop the address on the clipboard for paste.
// Phase 2: swap this for a direct PirateShip Ship API call (https://www.pirateship.com/ship-api).
const PIRATESHIP_URL = 'https://ship.pirateship.com/ship';

type ShippingMethod = 'usps_first_class' | 'usps_priority' | 'ups_ground';
type ShippingStatus = 'needs_boxing' | 'boxed' | 'label_printed' | 'shipped';

interface ShippingData {
  method?: ShippingMethod;
  boxLength?: number;
  boxWidth?: number;
  boxHeight?: number;
  weightLbs?: number;
  weightOz?: number;
  status?: ShippingStatus;
  trackingNumber?: string;
  labelUrl?: string;
  shippedAt?: string;
}

interface InvoiceItem {
  lotNumber?: string;
  sku?: string;
  title?: string;
  quantity?: number;
  highBid?: number;
  hammerPrice?: number;
  notes?: string;
  storageLocation?: string;
  images?: string[];
  imageUrl?: string;
}

interface Invoice {
  id: string; // Firestore doc id
  invoiceNumber: string;
  auctionId: string;
  auctionTitle?: string;
  bidderNumber: string;
  bidder: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
  items: InvoiceItem[];
  lotCount?: number;
  storageLocation?: string; // warehouse slot / "shipper" bin; set by the picker flow
  shipping?: ShippingData;
}

// Local, unsaved form values per invoice (dimensions/weight/method before "Mark Boxed")
interface BoxForm {
  boxLength: string;
  boxWidth: string;
  boxHeight: string;
  weightLbs: string;
  weightOz: string;
  method: ShippingMethod | '';
}

type StatusFilter = 'all' | ShippingStatus;
type SortKey = 'invoice' | 'lotCount' | 'buyer';

const METHOD_LABELS: Record<ShippingMethod, string> = {
  usps_first_class: 'USPS First Class',
  usps_priority: 'USPS Priority',
  ups_ground: 'UPS Ground',
};

const STATUS_META: Record<ShippingStatus, { label: string; badge: string; emoji: string }> = {
  needs_boxing: { label: 'NEEDS BOXING', badge: 'bg-orange-100 text-orange-800 border-orange-300', emoji: '⬜' },
  boxed: { label: 'BOXED', badge: 'bg-blue-100 text-blue-800 border-blue-300', emoji: '📦' },
  label_printed: { label: 'LABEL PRINTED', badge: 'bg-purple-100 text-purple-800 border-purple-300', emoji: '🏷️' },
  shipped: { label: 'SHIPPED', badge: 'bg-green-100 text-green-800 border-green-300', emoji: '✅' },
};

const hasShipperNote = (inv: Invoice): boolean =>
  String(inv.storageLocation || '').toLowerCase().includes('shipper');

const getStatus = (inv: Invoice): ShippingStatus => inv.shipping?.status || 'needs_boxing';

const emptyForm = (): BoxForm => ({
  boxLength: '',
  boxWidth: '',
  boxHeight: '',
  weightLbs: '',
  weightOz: '',
  method: '',
});

const formFromShipping = (s?: ShippingData): BoxForm => ({
  boxLength: s?.boxLength != null ? String(s.boxLength) : '',
  boxWidth: s?.boxWidth != null ? String(s.boxWidth) : '',
  boxHeight: s?.boxHeight != null ? String(s.boxHeight) : '',
  weightLbs: s?.weightLbs != null ? String(s.weightLbs) : '',
  weightOz: s?.weightOz != null ? String(s.weightOz) : '',
  method: s?.method || '',
});

const formatAddress = (inv: Invoice): string => {
  const b = inv.bidder || {};
  const name = `${b.firstName || ''} ${b.lastName || ''}`.trim();
  const cityLine = [b.city, b.state].filter(Boolean).join(', ');
  const cityZip = [cityLine, b.zip].filter(Boolean).join(' ');
  return [name, b.address, cityZip, b.phone].filter(Boolean).join('\n');
};

const ShippingStation: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<Record<string, BoxForm>>({});
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortBy, setSortBy] = useState<SortKey>('invoice');
  // sku/lotNumber -> thumbnail image for the selected auction
  const [imageMap, setImageMap] = useState<Record<string, string>>({});

  // The sale being shipped. 'fulfillment' — never follows a live sale.
  const selection = useSaleSelection({ role: 'fulfillment', storageKey: 'shipping' });
  const { auction, saleId, loading: salesLoading } = selection;

  const loadData = useCallback(async () => {
    if (!saleId || !auction) { setLoading(false); return; }
    setLoading(true);
    try {
      // Lots are embedded in the auction doc the hook already loaded — the extra
      // getDoc this used to make was a second read of the same document.
      const map: Record<string, string> = {};
      {
        const adata: any = auction;
        (adata.lots || []).forEach((lot: any) => {
          if (!lot.images || lot.images.length === 0) return;
          const img01 = lot.images.find((i: string) => i.includes('_01.jpg') || i.includes('_01.'));
          const thumb = img01 || (lot.images.length > 1 ? lot.images[1] : lot.images[0]);
          if (lot.sku != null) map[String(lot.sku)] = thumb;
          if (lot.lotNumber != null) map[String(lot.lotNumber)] = thumb;
        });
      }
      setImageMap(map);

      const snap = await getDocs(collection(db, 'invoices'));
      const result: Invoice[] = [];
      snap.forEach((d) => {
        const data = d.data() as Omit<Invoice, 'id'>;
        const inv: Invoice = { ...data, id: d.id };
        if (inv.auctionId !== saleId) return;
        if (!hasShipperNote(inv)) return;
        result.push(inv);
      });

      setInvoices(result);

      // Seed each form from any saved shipping data.
      const seeded: Record<string, BoxForm> = {};
      result.forEach((inv) => {
        seeded[inv.id] = formFromShipping(inv.shipping);
      });
      setForms(seeded);
    } catch (err) {
      console.error('Error loading shipping orders:', err);
      toast.error('Failed to load shipping orders');
    } finally {
      setLoading(false);
    }
  }, [saleId, auction]);

  // Reloads when the operator switches sale — the whole point of the selector.
  useEffect(() => { loadData(); }, [loadData]);

  const stats = useMemo(() => {
    let boxed = 0;
    let labels = 0;
    let shipped = 0;
    invoices.forEach((inv) => {
      const s = getStatus(inv);
      if (s === 'boxed') boxed++;
      if (s === 'label_printed') labels++;
      if (s === 'shipped') shipped++;
    });
    return { total: invoices.length, boxed, labels, shipped };
  }, [invoices]);

  const visibleInvoices = useMemo(() => {
    let list = invoices.filter((inv) => statusFilter === 'all' || getStatus(inv) === statusFilter);
    list = [...list].sort((a, b) => {
      if (sortBy === 'lotCount') {
        return (b.lotCount ?? b.items.length) - (a.lotCount ?? a.items.length);
      }
      if (sortBy === 'buyer') {
        return `${a.bidder?.lastName || ''}`.localeCompare(`${b.bidder?.lastName || ''}`);
      }
      // invoice number, numeric-aware
      const an = parseInt(a.invoiceNumber, 10);
      const bn = parseInt(b.invoiceNumber, 10);
      if (!isNaN(an) && !isNaN(bn)) return an - bn;
      return a.invoiceNumber.localeCompare(b.invoiceNumber);
    });
    return list;
  }, [invoices, statusFilter, sortBy]);

  const setForm = (id: string, patch: Partial<BoxForm>) => {
    setForms((prev) => ({ ...prev, [id]: { ...(prev[id] || emptyForm()), ...patch } }));
  };

  // Persist shipping data on the invoice doc and update local state.
  const patchShipping = async (inv: Invoice, patch: ShippingData, successMsg: string) => {
    const next: ShippingData = { ...(inv.shipping || {}), ...patch };
    try {
      await updateDoc(doc(db, 'invoices', inv.id), {
        shipping: next,
        updatedAt: new Date().toISOString(),
      });
      setInvoices((prev) => prev.map((p) => (p.id === inv.id ? { ...p, shipping: next } : p)));
      if (successMsg) toast.success(successMsg);
      return true;
    } catch (err) {
      console.error('Error updating shipping:', err);
      toast.error('Update failed');
      return false;
    }
  };

  const handleMarkBoxed = async (inv: Invoice) => {
    const f = forms[inv.id] || emptyForm();
    const l = parseFloat(f.boxLength);
    const w = parseFloat(f.boxWidth);
    const h = parseFloat(f.boxHeight);
    const lbs = parseFloat(f.weightLbs) || 0;
    const oz = parseFloat(f.weightOz) || 0;

    if (!l || !w || !h) {
      toast.error('Enter box length, width, and height');
      return;
    }
    if (lbs <= 0 && oz <= 0) {
      toast.error('Enter a weight');
      return;
    }
    if (!f.method) {
      toast.error('Select a shipping method');
      return;
    }

    await patchShipping(
      inv,
      {
        method: f.method,
        boxLength: l,
        boxWidth: w,
        boxHeight: h,
        weightLbs: lbs,
        weightOz: oz,
        status: 'boxed',
      },
      `Invoice #${inv.invoiceNumber} marked as boxed`
    );
  };

  const openPirateShip = (inv: Invoice) => {
    const address = formatAddress(inv);
    try {
      navigator.clipboard?.writeText(address);
    } catch {
      /* clipboard may be blocked; address is still shown on the card */
    }
    window.open(PIRATESHIP_URL, '_blank', 'noopener,noreferrer');
  };

  const handlePrintLabel = async (inv: Invoice) => {
    if (getStatus(inv) === 'needs_boxing') {
      toast.error('Mark the order as boxed first');
      return;
    }
    openPirateShip(inv);
    await patchShipping(inv, { status: 'label_printed' }, 'Address copied — paste into PirateShip');
  };

  const handleMarkShipped = async (inv: Invoice) => {
    await patchShipping(inv, { status: 'shipped', shippedAt: new Date().toISOString() }, `Invoice #${inv.invoiceNumber} shipped`);
  };

  const handlePrintAllLabels = async () => {
    const boxed = invoices.filter((inv) => getStatus(inv) === 'boxed');
    if (boxed.length === 0) {
      toast.error('No boxed orders ready for labels');
      return;
    }
    const block = boxed.map((inv) => formatAddress(inv)).join('\n\n');
    try {
      navigator.clipboard?.writeText(block);
    } catch {
      /* ignore */
    }
    window.open(PIRATESHIP_URL, '_blank', 'noopener,noreferrer');
    for (const inv of boxed) {
      // eslint-disable-next-line no-await-in-loop
      await patchShipping(inv, { status: 'label_printed' }, '');
    }
    toast.success(`${boxed.length} addresses copied — marked label printed`);
  };

  const handleMarkAllShipped = async () => {
    const ready = invoices.filter((inv) => getStatus(inv) === 'label_printed');
    if (ready.length === 0) {
      toast.error('No printed labels waiting to ship');
      return;
    }
    for (const inv of ready) {
      // eslint-disable-next-line no-await-in-loop
      await patchShipping(inv, { status: 'shipped', shippedAt: new Date().toISOString() }, '');
    }
    toast.success(`Marked ${ready.length} orders as shipped`);
  };

  const thumbFor = (item: InvoiceItem): string | null => {
    if (item.imageUrl) return item.imageUrl;
    if (item.images && item.images.length > 0) {
      const i01 = item.images.find((i) => i.includes('_01.jpg') || i.includes('_01.'));
      return i01 || (item.images.length > 1 ? item.images[1] : item.images[0]);
    }
    const key = item.sku != null ? String(item.sku) : item.lotNumber != null ? String(item.lotNumber) : '';
    return imageMap[key] || imageMap[String(item.lotNumber)] || null;
  };

  if (salesLoading || loading) {
    return (
      <div className="max-w-6xl mx-auto p-6 text-center">
        <p className="text-gray-600">Loading shipping orders...</p>
      </div>
    );
  }

  const filterButtons: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'Show All' },
    { key: 'needs_boxing', label: 'Needs Boxing' },
    { key: 'boxed', label: 'Boxed' },
    { key: 'label_printed', label: 'Label Printed' },
    { key: 'shipped', label: 'Shipped' },
  ];

  const sortButtons: { key: SortKey; label: string }[] = [
    { key: 'invoice', label: 'Invoice #' },
    { key: 'lotCount', label: 'Lot Count' },
    { key: 'buyer', label: 'Buyer Name' },
  ];

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">📦 Shipping Station</h1>
        <p className="text-gray-800 mt-1 font-medium">
          Orders to Ship: <span className="text-orange-600">{stats.total}</span>
          {'  |  '}Boxed: <span className="text-blue-600">{stats.boxed}</span>
          {'  |  '}Labels Printed: <span className="text-purple-600">{stats.labels}</span>
          {'  |  '}Shipped: <span className="text-green-600">{stats.shipped}</span>
        </p>
      </div>

      {/* Which sale these boxes belong to — stated, always, not inferred from a title. */}
      <SaleContextBanner selection={selection} label="Shipping" />

      {/* Filters + sort + batch actions */}
      <div className="bg-white rounded-lg shadow p-4 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold mr-1">Filter:</span>
          {filterButtons.map((b) => (
            <button
              key={b.key}
              onClick={() => setStatusFilter(b.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                statusFilter === b.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold mr-1">Sort by:</span>
          {sortButtons.map((b) => (
            <button
              key={b.key}
              onClick={() => setSortBy(b.key)}
              className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition ${
                sortBy === b.key ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-t pt-4">
          <span className="font-semibold mr-1">Batch:</span>
          <button
            onClick={handlePrintAllLabels}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700"
          >
            🏷️ Print All Labels ({stats.boxed})
          </button>
          <button
            onClick={handleMarkAllShipped}
            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
          >
            ✅ Mark All Shipped ({stats.labels})
          </button>
        </div>
      </div>

      {/* Orders */}
      {visibleInvoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-600">
          No orders match this filter.
        </div>
      ) : (
        <div className="space-y-5">
          {visibleInvoices.map((inv) => {
            const status = getStatus(inv);
            const meta = STATUS_META[status];
            const f = forms[inv.id] || emptyForm();
            const lots = inv.lotCount ?? inv.items.length;
            const b = inv.bidder || {};

            return (
              <div key={inv.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                {/* Card header */}
                <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 bg-gray-50 border-b">
                  <div className="font-bold text-lg">
                    Invoice #{inv.invoiceNumber} — {b.firstName} {b.lastName} —{' '}
                    <span className="text-gray-600 font-medium">{lots} lots</span>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-bold border ${meta.badge}`}>
                    {meta.emoji} {meta.label}
                  </span>
                </div>

                <div className="p-5 grid md:grid-cols-3 gap-5">
                  {/* Left: address + thumbnails */}
                  <div className="md:col-span-2 space-y-4">
                    {/* Address — prominent for label verification */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="text-xs font-semibold text-blue-700 uppercase mb-1">Ship To</div>
                      <div className="font-bold text-blue-900">
                        {b.firstName} {b.lastName}
                      </div>
                      {b.address ? (
                        <div className="text-sm text-blue-900">{b.address}</div>
                      ) : (
                        <div className="text-sm text-red-600 font-medium">⚠️ No street address on file</div>
                      )}
                      {(b.city || b.state || b.zip) && (
                        <div className="text-sm text-blue-900">
                          {[b.city, b.state].filter(Boolean).join(', ')} {b.zip}
                        </div>
                      )}
                      {b.phone && <div className="text-sm text-blue-700 mt-1">{b.phone}</div>}
                      {b.email && <div className="text-sm text-blue-700">{b.email}</div>}
                    </div>

                    {/* Thumbnails */}
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Items ({inv.items.length})</div>
                      <div className="flex flex-wrap gap-2">
                        {inv.items.map((item, idx) => {
                          const thumb = thumbFor(item);
                          return (
                            <div
                              key={`${inv.id}-${item.sku || item.lotNumber || idx}`}
                              className="w-16 h-16 rounded border border-gray-200 overflow-hidden bg-gray-100 flex items-center justify-center"
                              title={`Lot ${item.lotNumber || item.sku || ''} — ${item.title || ''}`}
                            >
                              {thumb ? (
                                <img src={thumb} alt={item.title || ''} className="w-full h-full object-cover" />
                              ) : (
                                <span className="text-[10px] text-gray-400 text-center px-1">
                                  {item.lotNumber || item.sku || 'Lot'}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="inline-block mt-2 text-sm text-blue-600 hover:text-blue-800"
                      >
                        📄 View full invoice
                      </Link>
                    </div>
                  </div>

                  {/* Right: box form */}
                  <div className="space-y-4">
                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Box Dimensions (inches)</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={f.boxLength}
                          onChange={(e) => setForm(inv.id, { boxLength: e.target.value })}
                          placeholder="L"
                          className="w-16 px-2 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-gray-400">×</span>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={f.boxWidth}
                          onChange={(e) => setForm(inv.id, { boxWidth: e.target.value })}
                          placeholder="W"
                          className="w-16 px-2 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-gray-400">×</span>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={f.boxHeight}
                          onChange={(e) => setForm(inv.id, { boxHeight: e.target.value })}
                          placeholder="H"
                          className="w-16 px-2 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Weight</div>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={f.weightLbs}
                          onChange={(e) => setForm(inv.id, { weightLbs: e.target.value })}
                          placeholder="0"
                          className="w-16 px-2 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">lbs</span>
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          value={f.weightOz}
                          onChange={(e) => setForm(inv.id, { weightOz: e.target.value })}
                          placeholder="0"
                          className="w-16 px-2 py-2 border border-gray-300 rounded text-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-600">oz</span>
                      </div>
                    </div>

                    <div>
                      <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Shipping Method</div>
                      <div className="space-y-1">
                        {(Object.keys(METHOD_LABELS) as ShippingMethod[]).map((m) => (
                          <label key={m} className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="radio"
                              name={`method-${inv.id}`}
                              checked={f.method === m}
                              onChange={() => setForm(inv.id, { method: m })}
                              className="text-blue-600 focus:ring-blue-500"
                            />
                            {METHOD_LABELS[m]}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-1">
                      <button
                        onClick={() => handleMarkBoxed(inv)}
                        className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700"
                      >
                        📦 {status === 'needs_boxing' ? 'Mark as Boxed' : 'Update Box Info'}
                      </button>
                      <button
                        onClick={() => handlePrintLabel(inv)}
                        disabled={status === 'needs_boxing'}
                        className="w-full px-3 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        🏷️ Print Label
                      </button>
                      {status === 'label_printed' && (
                        <button
                          onClick={() => handleMarkShipped(inv)}
                          className="w-full px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700"
                        >
                          ✅ Mark Shipped
                        </button>
                      )}
                      {inv.shipping?.trackingNumber && (
                        <div className="text-xs text-gray-600 text-center">Tracking: {inv.shipping.trackingNumber}</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ShippingStation;
